import { useState } from "react"
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Tooltip,
  IconButton,
  InputAdornment,
  Paper,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material"
import * as XLSX from "xlsx"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useAuth } from "../../contexts/AuthContext"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../utils/api"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"

function FeesPendingJobsPage() {
  const [selectedJob, setSelectedJob] = useState(null)
  const [jobDetailsForView, setJobDetailsForView] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false)
  const [comment, setComment] = useState("")
  const [searchTerm, setSearchTerm] = useState("")

  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: feesPendingJobs, isLoading } = useApiQuery(
    ["feesPendingJob", user?._id || user?.id, user?.role],
    `jobs/fees-pending`
  )

  const filteredJobs = (feesPendingJobs || []).filter((job) => {
    const s = searchTerm.toLowerCase()
    const matchesJobId = job.JobId?.toLowerCase().includes(s)
    const matchesClient = job.clientName?.toLowerCase().includes(s)
    const matchesAssigned = user?.role === "admin" && job.assignedToName?.toLowerCase().includes(s)
    return !searchTerm || matchesJobId || matchesClient || matchesAssigned
  })

  const handleExportToExcel = () => {
    const allJobs = filteredJobs

    const excelData = allJobs.map((job) => ({
      "Job ID": job.JobId,
      "Client Name": job.clientName,
      "Phone Number": job.clientPhoneNumber || "N/A",
      "Job Type": job.jobType,
      "Due Date": job.dueDate ? new Date(job.dueDate).toLocaleDateString() : "N/A",
      "Completed On": job.completedAt ? new Date(job.completedAt).toLocaleDateString() : (job.updatedAt ? new Date(job.updatedAt).toLocaleDateString() : "N/A"),
      "Job Status": job.status,
      "Fees (₹)": job.fees ?? 0,
      "Fees Status": job.feeStatus,
      "Assigned To": job.assignedToName || "Unassigned",
    }))

    const worksheet = XLSX.utils.json_to_sheet(excelData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pending Fees")

    const columnWidths = [
      { wch: 15 }, // Job ID
      { wch: 25 }, // Client Name
      { wch: 15 }, // Phone Number
      { wch: 25 }, // Job Type
      { wch: 14 }, // Due Date
      { wch: 14 }, // Completed On
      { wch: 14 }, // Job Status
      { wch: 12 }, // Fees
      { wch: 14 }, // Fees Status
      { wch: 22 }, // Assigned To
    ]
    worksheet["!cols"] = columnWidths

    const fileName = `Pending_Fees_Jobs_${new Date().toISOString().split("T")[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
  }

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        setAddCommentDialogOpen(false)
        setComment("")
        queryClient.invalidateQueries({ queryKey: ["feesPendingJob"] })
      },
    }
  )

  // Define table columns with custom render for completed date
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    {
      id: "client",
      label: "Client",
      render: (job) => (
        <Box>
          <Typography variant="subtitle2">{job.clientName}</Typography>
          {job.clientPhoneNumber && (
            <Typography variant="caption" color="textSecondary">
              {job.clientPhoneNumber}
            </Typography>
          )}
        </Box>
      ),
    },
    { id: "jobType", label: "Job Type", field: "jobType" },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    {
      id: "completedOn",
      label: "Completed On",
      render: (job) => {
        const date = job.completedAt || job.updatedAt
        return date ? new Date(date).toLocaleDateString() : "-"
      },
    },
    {
      id: "status",
      label: "Status",
      render: (job) => (
        <Chip label="Completed" color="success" size="small" />
      ),
    },
    {
      id: "fees",
      label: "Fees",
      render: (job) => (
        <Chip label={`${job?.fees}`} color="default" size="small" />
      ),
    },
    {
      id: "assignedTo",
      label: "Assigned To",
      render: (job) => (
        <Box>
          <Typography variant="body2">
            {job.assignedToName || "Unassigned"}
          </Typography>
          {job.secondaryUser && job.secondaryUser.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              + {job.secondaryUser.map((u) => u.name).join(", ")}
            </Typography>
          )}
        </Box>
      ),
    },
  ]

  // Define actions
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = async (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        // Fetch full job details
        try {
          const jobDetails = await api.get(`/jobs/${job.JobId}`)
          setJobDetailsForView(jobDetails.data[0])
          setViewDialogOpen(true)
        } catch (error) {
          console.error("Failed to fetch job details:", error)
        }
        break
      case "addComment":
        setAddCommentDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleAddCommentSave = async () => {
    try {
      if (!comment) return
      const data = { commentText: comment }
      await setJobCommentMutation.mutateAsync(data)
    } catch (error) {
      console.error("Failed to add comment", error)
    }
  }

  if (isLoading) {
    return <Typography>Loading pending fees jobs...</Typography>
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Pending Fees Jobs
          </Typography>
        </Box>
        {/* <Tooltip title="Export to Excel">
          <IconButton
            onClick={handleExportToExcel}
            color="success"
            disabled={!feesPendingJobs || feesPendingJobs.length === 0}
          >
            <FileDownloadIcon />
          </IconButton>
        </Tooltip> */}

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={filteredJobs.length === 0}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>

      {/* Search bar */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          placeholder={user?.role === "admin" ? "Search by Job ID, client name or assignee..." : "Search by Job ID or client name..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          fullWidth
          size="small"
        />
      </Paper>

      {/* Jobs Table - Reusable Component */}
      <JobsTable
        jobs={filteredJobs}
        columns={columns}
        actions={actions}
        onActionClick={handleActionClick}
        emptyMessage="No completed jobs found."
      />

      {/* View Details Dialog - Reusable Component */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setJobDetailsForView(null)
          setSelectedJob(null)
        }}
        job={jobDetailsForView}
      />

      {/* Add Comment Dialog */}
      <Dialog
        open={addCommentDialogOpen}
        onClose={() => setAddCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add notes about the completed job..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCommentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddCommentSave}
            variant="contained"
            disabled={!comment || setJobCommentMutation.isPending}
          >
            {setJobCommentMutation.isPending ? "Adding..." : "Add Comment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default FeesPendingJobsPage